import { type TableProps, useMediaQuery } from "@kyan/ui";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { useKyanWsErrors } from "#/store/app/kyanWsErrors/kyanWsErrors";
import { useMarket } from "#/store/app/portfolio/market";
import { useShowTradePanel } from "#/store/app/tradePanel/showTradePanel";
import { useTradePanelTab } from "#/store/app/tradePanel/tradePanelTab";
import { useComboActiveInstrumentId } from "#/store/combo/comboActiveInstrumentId";
import type { ComboInstrument } from "#/store/combo/comboInstruments";
import { useComboInstruments } from "#/store/combo/comboInstruments";
import { useMaturity } from "#/store/options/page/maturity";
import { useOptionchain } from "#/store/options/page/optionchain";
import { useOptionType } from "#/store/options/page/optionType";
import { useTradeDirection } from "#/store/options/page/tradeDirection";
import { useOption } from "#/store/options/tradePanel/option";
import { type Option, type OptionChainEntry } from "#/types/options";
import { TradeDirection } from "#/types/trade";
import { getOptionInstrumentProperties } from "#/utils/instrument.utils";
import { ChannelType } from "#/worker-v2/types/protocol/subscriptions";

import type { Columns } from "./components/ColumnsDropdown";
import {
  desktopDefaultShownColumns,
  mobileDefaultShownColumns,
} from "./OptionChain.constants";
import type { OptionChainColumnGroup } from "./OptionChain.types";
import {
  calculateAroundATM,
  calculateDist,
  getColumns,
  getGroupIdFromComboInstrument,
  getGroupIdFromOption,
} from "./OptionChain.utils";

export const useOptionChainController = () => {
  const { market } = useMarket();
  const { maturity } = useMaturity();
  const { isMd, isLg } = useMediaQuery();
  const { optionType } = useOptionType();
  const { optionchain } = useOptionchain();
  const { option, setOption } = useOption();
  const { kyanWsErrors } = useKyanWsErrors();
  const { tradeDirection } = useTradeDirection();
  const { setShowTradePanel } = useShowTradePanel();
  const { marketIndexPrice } = useMarketIndexPrice();
  const { comboOptionInstruments } = useComboInstruments();
  const { tradePanelTab, setTradePanelTab } = useTradePanelTab();
  const { comboActiveInstrumentId } = useComboActiveInstrumentId();
  const { handleAddLeg, handleEditLeg } = useComboInstrumentsController();

  const hasError = kyanWsErrors[ChannelType.OrderbookOptions];

  const isOnComboTab = tradePanelTab === "combo";
  let activeGroupIds: string[] = [];

  if (isOnComboTab) {
    activeGroupIds = comboOptionInstruments.map((instrument) =>
      getGroupIdFromComboInstrument(instrument),
    );
  } else {
    activeGroupIds = option ? [getGroupIdFromOption(option)] : [];
  }

  const [dist, setDist] = useState(false);
  const [aroundATM, setAroundATM] = useState(false);
  const [shownColumns, setShownColumns] = useState<Columns>(
    desktopDefaultShownColumns,
  );

  useEffect(() => {
    setShownColumns(
      isLg ? desktopDefaultShownColumns : mobileDefaultShownColumns,
    );
  }, [isLg]);

  const onGroupClick: TableProps<
    OptionChainEntry,
    OptionChainColumnGroup
  >["onGroupClick"] = ({ groupId, row }) => {
    const [instrumentName, side] = groupId.split(":");

    const isBuy = side === "ask";
    const direction: TradeDirection = isBuy ? "buy" : "sell";
    const { strike, optionType: instrumentOptionType } =
      getOptionInstrumentProperties(instrumentName);
    const isCall = instrumentOptionType === "call";
    const delta = isCall ? row.callDelta : row.putDelta;
    const mark = isCall ? row.callMark : row.putMark;

    if (isOnComboTab) {
      const newInstrument: ComboInstrument = {
        id: uuid(),
        type: "options",
        side: direction,
        underlying: market.underlying,
        optionType: instrumentOptionType,
        maturity,
        strike,
        legPrice: 60_000,
        legRatio: 1,
        size: "",
      };

      if (activeGroupIds.includes(getGroupIdFromComboInstrument(newInstrument)))
        return;

      comboActiveInstrumentId
        ? handleEditLeg({ ...newInstrument, id: comboActiveInstrumentId })
        : handleAddLeg(newInstrument);
      setShowTradePanel(true);
      return;
    }

    const optionObject: Option = {
      ...(instrumentName === option?.instrument_name ? option : {}),
      instrument_name: instrumentName,
      isBuy,
      isCall,
      underlying: market.underlying,
      direction,
      maturity,
      markPrice: mark,
      strike: Number(strike),
      marketPair: market.pair,
      delta,
    };

    setOption(optionObject);

    setTradePanelTab("options");
    /// @dev Purposefully only adding the timer here
    setTimeout(() => {
      setShowTradePanel(true);
    }, 175);
  };

  const columns = getColumns({
    isMd,
    isLg,
    optionType,
    tradeDirection,
  });

  const filteredColumns = useMemo(
    () =>
      columns.filter((column) => {
        const columnId = column.id.toLowerCase();

        return Object.entries(shownColumns).every(
          ([key, value]) =>
            Boolean(value) || !columnId.includes(key.toLowerCase()),
        );
      }),
    [columns, shownColumns],
  );

  const optionChainData = useMemo(() => {
    let transformedData = optionchain;

    if (dist)
      transformedData = calculateDist(transformedData, marketIndexPrice);
    if (aroundATM)
      transformedData = calculateAroundATM(transformedData, marketIndexPrice);

    return transformedData;
  }, [optionchain, dist, marketIndexPrice, aroundATM]);

  return {
    dist,
    market,
    maturity,
    hasError,
    aroundATM,
    isLoading: optionchain.length === 0,
    shownColumns,
    activeGroupIds,
    filteredColumns,
    optionChainData,
    marketIndexPrice,
    setDist,
    setAroundATM,
    onGroupClick,
    setShownColumns,
  };
};
